import React, { useEffect, useRef, useCallback } from "react";
import MobileNavigation from "./MobileNavigation";
import {
  animateOverlayOpen,
  animateMenuItemsEnter,
  animateMenuItemsExit,
  animateCloseButtonEnter,
  animateCloseButtonExit,
} from "./animations/mobile-animations";
import {
  BUSINESS_SOCIAL_MEDIA,
  SOCIAL_MEDIA_ICONS,
} from "@/core/constants/business";

interface MobileMenuOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenuOverlay = ({ isOpen, onClose }: MobileMenuOverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isAnimatingRef = useRef(false);

  // Memoized close handler to prevent unnecessary re-renders
  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!isAnimatingRef.current) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen && !isAnimatingRef.current) {
      isAnimatingRef.current = true;

      // Animate the overlay and menu items
      animateOverlayOpen(overlayRef, () => {
        animateMenuItemsEnter();
        isAnimatingRef.current = false;
      });

      // Animate the close button
      animateCloseButtonEnter(closeButtonRef);
    } else if (!isOpen && !isAnimatingRef.current) {
      isAnimatingRef.current = true;

      // Animate the close button out
      animateCloseButtonExit(closeButtonRef);

      // Animate menu items and overlay out
      animateMenuItemsExit(overlayRef, () => {
        isAnimatingRef.current = false;
      });
    }
  }, [isOpen]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEscKey);
      return () => window.removeEventListener("keydown", handleEscKey);
    }
  }, [isOpen, handleClose]);

  // Don't render if not open and not animating
  if (!isOpen && overlayRef.current?.style.opacity === "0") {
    return null;
  }

  // Create instances of icon components
  const InstagramIcon = SOCIAL_MEDIA_ICONS.instagram;
  const YoutubeIcon = SOCIAL_MEDIA_ICONS.youtube;
  const FacebookIcon = SOCIAL_MEDIA_ICONS.facebook;
  const TiktokIcon = SOCIAL_MEDIA_ICONS.tiktok;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-law-black z-40 opacity-0 h-0 overflow-hidden"
      style={{
        height: 0,
        willChange: "opacity, height", // Hardware acceleration hint
      }}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation menu"
    >
      {/* Close button */}
      <button
        ref={closeButtonRef}
        className="absolute top-6 right-6 text-white hover:text-cannabis-green-300 transition-colors duration-300 z-50 p-2"
        onClick={handleClose}
        aria-label="Close menu"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div
        className="container mx-auto h-full px-4 py-20"
        ref={menuItemsRef}
        onClick={(e) => e.stopPropagation()}
      >
        <MobileNavigation onLinkClick={handleClose} />

        <div className="absolute bottom-10 left-0 w-full text-center">
          <div className="flex justify-center space-x-6 mb-8">
            {/* Social Media Icons using the business constants */}
            <a
              href={BUSINESS_SOCIAL_MEDIA.instagram || "#"}
              className="text-white hover:text-cannabis-green-300 transition-colors p-2"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram"
            >
              <InstagramIcon size={24} weight="fill" />
            </a>

            <a
              href={BUSINESS_SOCIAL_MEDIA.youtube || "#"}
              className="text-white hover:text-cannabis-green-300 transition-colors p-2"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Subscribe to our YouTube"
            >
              <YoutubeIcon size={24} weight="fill" />
            </a>

            <a
              href={BUSINESS_SOCIAL_MEDIA.tiktok || "#"}
              className="text-white hover:text-cannabis-green-300 transition-colors p-2"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on TikTok"
            >
              <TiktokIcon size={24} weight="fill" />
            </a>

            <a
              href={BUSINESS_SOCIAL_MEDIA.facebook || "#"}
              className="text-white hover:text-cannabis-green-300 transition-colors p-2"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Like us on Facebook"
            >
              <FacebookIcon size={24} weight="fill" />
            </a>
          </div>

          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Street Lawyer Services. All rights
            reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MobileMenuOverlay);
